// FinancingTab.js - Financing offers configuration
import {
  React,
  useEffect,
  useState,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
} from './PromoFinancingCommon';
import { useAppFlags } from '../utils/AppFlagsContext';

const FinancingTab = () => {
  const { flags, refresh: refreshFlags } = useAppFlags();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [filterModel, setFilterModel] = useState({ items: [] });

  const fetchData = () => {
    setLoading(true);
    authFetch('/financing/')
      .then(data => { setRows(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/financing/${editData.id}`, formData, 'PUT');
        setRows(prevRows => prevRows.map(row => row.id === editData.id ? { ...formData, id: editData.id } : row));
        if (selectedRow?.id === editData.id) {
          setSelectedRow({ ...formData, id: editData.id });
        }
      } else {
        updatedRow = await authFetchRequest('/financing/', formData, 'POST');
        if (updatedRow && updatedRow.id) {
          setRows(prevRows => [...prevRows, updatedRow]);
        } else {
          fetchData();
        }
      }
      setOpenDialog(false);
      setEditData(null);
    } catch (err) {
      console.error('Save failed:', err);
      await alertC('Failed to save financing offer');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this financing offer?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/financing/${selectedRow.id}`, null, 'DELETE');
      setRows(prevRows => prevRows.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'provider', headerName: 'Provider', width: 200 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'details', headerName: 'Details', width: 200 },
    { field: 'months', headerName: 'Months', width: 80 },
    { field: 'interest_rate', headerName: 'Int. Rate', width: 80 },
    { field: 'website_url', headerName: 'Website URL', width: 150 },
    { field: 'expiration_date', headerName: 'Expiration Date', width: 120 },
    { field: 'available_to', headerName: 'Available To', width: 150 },
    { field: 'active', headerName: 'Active', width: 80, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  // Toggle the financing_enabled feature flag. Mirrors the promotions
  // checkbox in PromotionsTab.js.
  const handleToggleEnabled = async (e) => {
    const enabled = e.target.checked;
    try {
      await authFetchRequest('/settings/financing_enabled', { enabled }, 'PUT');
      refreshFlags();
    } catch (err) {
      alertC('Failed to update financing feature flag: ' + (err?.message || err));
    }
  };

  // While the feature is OFF, lock the data management below so admins
  // can't edit records for a feature that won't be visible anywhere.
  const featureOn = !!flags.financing_enabled;
  const disabledStyle = featureOn
    ? {}
    : { opacity: 0.45, pointerEvents: 'none' };

  return (
    <Box>
      <FormControlLabel
        sx={{ mb: 1 }}
        control={
          <Checkbox
            checked={featureOn}
            onChange={handleToggleEnabled}
          />
        }
        label="Enable Financing (show the Financing panel inside each Quote)"
      />
      <Box sx={disabledStyle}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" disabled={!featureOn} onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Financing Offer
        </Button>
        <Button variant="contained" color="secondary" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!featureOn || !selectedRow}>
          Edit Financing Offer
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete} disabled={!featureOn || !selectedRow}>
          Delete Financing Offer
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
        />
      )}

      <FinancingDialog
        open={openDialog}
        data={editData}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
      </Box>{/* end disabled-when-OFF wrapper */}
    </Box>
  );
};

const FinancingDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    provider: '',
    name: '',
    details: '',
    months: '',
    interest_rate: '',
    website_url: '',
    expiration_date: '',
    available_to: '',
    active: false
  });

  useEffect(() => {
    if (data) {
      setFormData({
        provider: data.provider || '',
        name: data.name || '',
        details: data.details || '',
        months: data.months || '',
        interest_rate: data.interest_rate || '',
        website_url: data.website_url || '',
        expiration_date: data.expiration_date || '',
        available_to: data.available_to || '',
        active: data.active ?? false
      });
    } else {
      setFormData({
        provider: '',
        name: '',
        details: '',
        months: '',
        interest_rate: '',
        website_url: '',
        expiration_date: '',
        available_to: '',
        active: false
      });
    }
  }, [data, open]);

  const handleSubmit = async () => {
    if (!formData.provider || formData.provider.trim() === '') {
      await alertC('Provider is required.');
      return;
    }
    if (!formData.name || formData.name.trim() === '') {
      await alertC('Name is required.');
      return;
    }
    if (formData.months !== '' && formData.months !== null) {
      const monthsValue = Number(formData.months);
      if (isNaN(monthsValue) || monthsValue < 0) {
        await alertC('Months must be a positive number.');
        return;
      }
      if (monthsValue > 84) {
        await alertC('Months cannot exceed 84.');
        return;
      }
    }
    if (formData.interest_rate !== '' && formData.interest_rate !== null) {
      const rateValue = Number(formData.interest_rate);
      if (isNaN(rateValue) || rateValue < 0) {
        await alertC('Interest Rate must be a positive number.');
        return;
      }
      if (rateValue > 100) {
        await alertC('Interest Rate cannot exceed 100%.');
        return;
      }
    }
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Financing Offer' : 'Add Financing Offer'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Provider"
            value={formData.provider}
            onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Details"
            value={formData.details}
            onChange={(e) => setFormData({ ...formData, details: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Months"
            type="number"
            value={formData.months}
            onChange={(e) => setFormData({ ...formData, months: e.target.value })}
            fullWidth
            inputProps={{ min: 0, max: 84 }}
          />
          <TextField
            label="Interest Rate (%)"
            type="number"
            value={formData.interest_rate}
            onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
            fullWidth
            inputProps={{ min: 0, max: 100, step: 0.01 }}
          />
          <TextField
            label="Website URL"
            type="url"
            value={formData.website_url}
            onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
            fullWidth
          />
          <TextField
            label="Expiration Date"
            type="date"
            value={formData.expiration_date}
            onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Available To"
            value={formData.available_to}
            onChange={(e) => setFormData({ ...formData, available_to: e.target.value })}
            fullWidth
          />
          <FormControlLabel
            control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />}
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinancingTab;
